import React, { useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { useTheme } from 'styled-components';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import CustomOffcanvas from '../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../components/CustomRow';
import Input from '../../../../../components/InputNew';
import InputMoney from '../../../../../components/InputNew/InputMoney';
import Select from '../../../../../components/InputNew/Select';
import Log from '../../../../../components/Log/Log';
import Tabela from '../../../../../components/Tabela';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { ProaqPrecoCMED } from '../../../../../modules/types/Proaq';
import {
  getStatusAtivoLabelPorSlug,
  getStatusAtivoOptions,
  getStatusAtivoPorCodigo,
  getStatusAtivoSlugPorValue,
} from '../../../../../modules/types/StatusAtivoEnum';
import { EnumOption, LogData } from '../../../../../modules/types/types';
import {
  createPrecoCMED,
  deletePrecoCMED,
  getPrecoCMEDsByProaqId,
  updatePrecoCMED,
} from '../../../../../services/processosAquisitivos/proaqPrecoCMEDService';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
  formatValorDinheiro,
  sanitizarForm,
} from '../../../../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../../../../utils/swal';
import {
  headersProaqPrecoCMED,
  valoresExibidosProaq,
} from '../../auxiliarProaq';

interface PesquisaPrecoProps {
  fichaID: string;
  userID: string;
  produtosDAFOptions: EnumOption[];
  fornecedorOptions: EnumOption[];
  fornecedoresData: any[];
  btDisable?: boolean;
}

const PesquisaPreco: React.FC<PesquisaPrecoProps> = ({
  fichaID,
  userID,
  produtosDAFOptions,
  fornecedorOptions,
  fornecedoresData,
  btDisable,
}) => {
  const theme = useTheme();
  const [showModalPrecoCMED, setShowModalPrecoCMED] = useState<boolean>(false);
  const [precoCMEDData, setPrecoCMEDData] = useState<ProaqPrecoCMED[]>([]);
  const [updatingPrecoCMED, setUpdatingPrecoCMED] = useState<boolean>(false);
  const [logPrecoCMEDData, setLogPrecoCMEDData] = useState<LogData>();

  const [formPreco, setFormPreco] = useState<ProaqPrecoCMED>({
    id: 0,
    produtoId: 0,
    fornecedorId: 0,
    dataPreco: '',
    isCMED: 0,
    preco: 0,
    observacoes: '',
    dataRegistro: '',
    dataUltimaAtualizacao: '',
    dataUltimaVisualizacao: '',
    nEdicoes: 0,
    nVisualizacoes: 0,
    usuarioRegistro: { nomeCompleto: '' },
    usuarioAtualizacao: { nomeCompleto: '' },
    usuarioUltimaVisualizacao: { nomeCompleto: '' },
    usuarioId: parseInt(userID ?? '0', 10),
  });

  const fetchPrecoCMED = async () => {
    if (fichaID === 'undefined') return;
    try {
      const data = await getPrecoCMEDsByProaqId(fichaID);
      setPrecoCMEDData(data || []);
    } catch (error) {
      console.error('Erro ao buscar preços CMED:', error);
      setPrecoCMEDData([]);
    }
  };

  useEffect(() => {
    fetchPrecoCMED();
  }, [fichaID]);

  const tableDataPrecoCMED = precoCMEDData.map((item: ProaqPrecoCMED) => ({
    id: item.id,
    dataPreco: formatDateSimp(item.dataPreco),
    produto: item.produtoDAF?.produtoFarmaceutico || 'Não informado',
    fornecedor: item.fornecedor?.razaoSocial || 'Não informado',
    preco: 'R$ ' + formatValorDinheiro(Number(item.preco).toFixed(2)),
  }));

  const offCanvasDataPrecoCMED = precoCMEDData.map((item: ProaqPrecoCMED) => ({
    id: item.id,
    dataRegistro:
      formatDate(item.dataRegistro) +
      ' - ' +
      formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
    dataUltimaAtualizacao:
      formatDate(item.dataUltimaAtualizacao) +
      ' - ' +
      formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),
    dataUltimaVisualizacao:
      formatDate(item.dataUltimaVisualizacao) +
      ' - ' +
      formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
    nEdicoes: item.nEdicoes,
    nVisualizacoes: item.nVisualizacoes,
    separadorLog: '',
    produto: <b>{item.produtoDAF?.produtoFarmaceutico}</b>,
    fornecedor: <b>{item.fornecedor?.razaoSocial}</b>,
    dataPreco: formatDateSimp(item.dataPreco),
    isCMED: item.isCMED ? (
      <b>{getStatusAtivoLabelPorSlug(item.isCMED.toString())}</b>
    ) : (
      '-'
    ),
    preco: item.preco ? (
      <b>{`R$ ${formatValorDinheiro(parseFloat(item.preco.toString()).toFixed(2))}`}</b>
    ) : (
      '-'
    ),
    separador: '',
    observacoes: item.observacoes,
  }));

  const handleClearFormPreco = () => {
    setFormPreco({
      id: 0,
      produtoId: 0,
      fornecedorId: 0,
      dataPreco: '',
      isCMED: 0,
      preco: 0,
      observacoes: '',
      dataRegistro: '',
      dataUltimaAtualizacao: '',
      dataUltimaVisualizacao: '',
      nEdicoes: 0,
      nVisualizacoes: 0,
      usuarioRegistro: { nomeCompleto: '' },
      usuarioAtualizacao: { nomeCompleto: '' },
      usuarioUltimaVisualizacao: { nomeCompleto: '' },
      usuarioId: parseInt(userID ?? '0', 10),
    });
    setLogPrecoCMEDData(undefined);
  };

  const handleShowModalPrecoCMED = () => {
    setShowModalPrecoCMED((prev) => !prev);
    if (showModalPrecoCMED) {
      handleClearFormPreco();
    }
  };

  const handleChangePrecoCMED = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    setFormPreco((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleRowClickPreco = (id: string) => {
    handleShowModalPrecoCMED();
    setUpdatingPrecoCMED(true);
    const filteredItem = precoCMEDData.find(
      (item) => item.id == parseInt(id, 10),
    );

    if (filteredItem) {
      setFormPreco({
        ...filteredItem,
        fornecedorId: filteredItem.fornecedor?.id || 0,
        produtoId: filteredItem.produtoDAF?.id || 0,
        preco:
          filteredItem.preco && filteredItem.preco != null
            ? parseFloat(filteredItem.preco.toString()).toFixed(4)
            : 0,
        isCMED: Number(getStatusAtivoPorCodigo(filteredItem.isCMED.toString())),
      });
      setLogPrecoCMEDData({
        id: filteredItem.id,
        usuarioRegistro: filteredItem.usuarioRegistro.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro,
        dataAtualizacao: filteredItem.dataUltimaAtualizacao,
        nEdicoes: filteredItem.nEdicoes,
      });
    }
  };

  const validateFormPreco = (form: ProaqPrecoCMED): boolean => {
    const erros: string[] = [];
    if (!form.dataPreco) erros.push('<b>Data do Preço</b> é obrigatória.');
    if (!form.preco) erros.push('<b>Preço do Produto</b> é obrigatório.');
    if (form.isCMED.toString() == 'NI')
      erros.push('<b>Preço CMED</b> deve ser informado.');
    if (form.produtoId == 0) erros.push('<b>Produto DAF</b> é obrigatório.');
    if (form.fornecedorId == 0) erros.push('<b>Fornecedor</b> é obrigatório.');

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }
    return true;
  };

  const formatFormPreco = (form: any) => ({
    ...form,
    proaqId: parseInt(fichaID, 10),
    produtoId: parseInt(form.produtoId, 10),
    fornecedorId: parseInt(form.fornecedorId, 10),
    preco: parseFloat(Number(form.preco).toFixed(4)),
    isCMED: getStatusAtivoSlugPorValue(form.isCMED),
  });

  const handleCreatePrecoCMED = async () => {
    try {
      const formAjustado = formatFormPreco(formPreco);
      const formEnvio = sanitizarForm(formAjustado);
      if (!validateFormPreco(formEnvio)) return;

      await createPrecoCMED(formEnvio);
      swalSave();
      fetchPrecoCMED();
      setTimeout(handleShowModalPrecoCMED, 2000);
    } catch (error: any) {
      const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
      swalErro(errorMessage);
    }
  };

  const handleUpdatePrecoCMED = async () => {
    try {
      const formAjustado = formatFormPreco(formPreco);
      const formEnvio = sanitizarForm(formAjustado);
      if (!validateFormPreco(formEnvio)) return;
      const response = await updatePrecoCMED(formPreco.id, formEnvio);
      swalAtualizacao('Sucesso', response.message);
      fetchPrecoCMED();
      setTimeout(handleShowModalPrecoCMED, 2000);
    } catch (error: any) {
      const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
      swalErro(errorMessage);
    }
  };

  const handleDeletePrecoCMED = async () => {
    const result = await swalDelete();
    if (result) {
      try {
        await deletePrecoCMED(formPreco.id);
        swalDeleteConfirm();
        fetchPrecoCMED();
        setTimeout(handleShowModalPrecoCMED, 2000);
      } catch (error: any) {
        const errorMessage =
          error.message ||
          'Não foi possível excluir o registro. Tente novamente.';
        swalErro(errorMessage);
      }
    }
  };

  return (
    <>
      <BoxConteudo
        boxTitulo="Pesquisa de Preço"
        fechado={fichaID === 'undefined'}
      >
        <CustomRow>
          <CustomCol width="100%">
            <Tabela
              headers={headersProaqPrecoCMED}
              dataTable={tableDataPrecoCMED}
              displayValues={valoresExibidosProaq}
              onRowClick={handleRowClickPreco}
              dataOffCanvas={offCanvasDataPrecoCMED}
              botaoAbrirOffCanvas={false}
              showPagination={false}
              noOffCanvasTable={true}
            />
          </CustomCol>
        </CustomRow>
        <Col className="d-flex justify-content-end">
          <CustomButton
            label="Novo Registro"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={() => {
              setUpdatingPrecoCMED(false);
              handleShowModalPrecoCMED();
            }}
            disabled={fichaID === 'undefined'}
          />
        </Col>
      </BoxConteudo>

      <CustomOffcanvas
        title="Pesquisa de Preço"
        show={showModalPrecoCMED}
        onClose={handleShowModalPrecoCMED}
        width="60vw"
      >
        <WorkspaceFicha>
          <Log
            {...logPrecoCMEDData}
            entity="TB_PROAQ_PRECO_CMED"
            id={Number(formPreco.id)}
          />
          <BoxConteudo boxTitulo="Pesquisa de Preço">
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol>
                <Select
                  name="produtoId"
                  value={formPreco.produtoId}
                  label="Produto Farmacêutico"
                  options={produtosDAFOptions}
                  onChange={handleChangePrecoCMED}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="220px">
                <Input
                  id="fornecedorCnpj"
                  name="fornecedorCnpj"
                  value={
                    formPreco.fornecedorId != 0
                      ? fornecedoresData.find(
                          (fornecedor) =>
                            fornecedor.id == formPreco.fornecedorId,
                        )?.cnpj || ''
                      : 'Não informado'
                  }
                  label="CNPJ"
                  readonly
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="fornecedorId"
                  value={formPreco.fornecedorId}
                  options={fornecedorOptions}
                  label="Fornecedor"
                  onChange={handleChangePrecoCMED}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow style={{ marginTop: '10px', justifyContent: 'left' }}>
              <CustomCol width="200px">
                <Select
                  name="isCMED"
                  value={formPreco.isCMED}
                  options={getStatusAtivoOptions()}
                  label="Preço CMED"
                  onChange={handleChangePrecoCMED}
                  required
                />
              </CustomCol>
              <CustomCol width="220px">
                <Input
                  type="date"
                  name="dataPreco"
                  value={formPreco.dataPreco}
                  id="dataPreco"
                  label="Data do Preço"
                  onChange={handleChangePrecoCMED}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <InputMoney
                  name="preco"
                  value={formPreco.preco.toString()}
                  id="preco"
                  label="Preço (R$)"
                  casasDecimais={4}
                  onChange={handleChangePrecoCMED}
                  textAlign="right"
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow style={{ marginTop: '10px' }}>
              <Col>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={formPreco.observacoes}
                  label="Observações"
                  linhas={3}
                  onChange={handleChangePrecoCMED}
                />
              </Col>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updatingPrecoCMED ? (
              <WorkspaceBotoes
                delete
                deleteDisabled={btDisable}
                onClickDelete={handleDeletePrecoCMED}
                save
                saveDisabled={btDisable}
                onClickSave={handleUpdatePrecoCMED}
              />
            ) : (
              <WorkspaceBotoes
                inserir
                inserirDisabled={btDisable}
                onClickInserir={handleCreatePrecoCMED}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </>
  );
};

export default PesquisaPreco;
